import React from 'react';
import {Select} from 'antd';
import {useTranslation} from '../../hooks/useTranslation';

interface SelectYesNoProps {
  value?: string | boolean;
  placeholder?: string;
  onChange?: (value: string | boolean) => void;
  disabled?: boolean;
  allowClear?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  className?: string;
  id?: string;
  returnType?: 'string' | 'boolean';
  stringFormat?: 'yes/no' | 'S/N';
}

const SelectYesNo: React.FC<SelectYesNoProps> = ({
  value,
  placeholder,
  onChange,
  disabled = false,
  allowClear = true,
  size = 'middle',
  style,
  className,
  id,
  returnType = 'string',
  stringFormat = 'yes/no'
}) => {
  const {t} = useTranslation();

  // Normalize incoming values (boolean, 'yes'/'no', 'S'/'N', 'si'/'no', etc.)
  const normalizeToYesNo = (v: any): 'yes' | 'no' | undefined => {
    if (v === undefined || v === null || v === '') return undefined;
    if (typeof v === 'boolean') return v ? 'yes' : 'no';
    const s = String(v).trim().toLowerCase();
    if (s === 'yes' || s === 'y' || s === 'true' || s === 'si') return 'yes';
    if (s === 'no' || s === 'n' || s === 'false') return 'no';
    // Accept Spanish-style 'S'/'N' (case-insensitive)
    if (s === 's') return 'yes';
    if (s === 'n') return 'no';
    return undefined;
  };

  const displayValue = normalizeToYesNo(value);

  const handleChange = (selectedValue: string | undefined) => {
    if (selectedValue === undefined || selectedValue === null) {
      // Handle clear action
      if (onChange) {
        onChange(returnType === 'boolean' ? false : '');
      }
      return;
    }

    // the selected Value will be 'yes' or 'no' as those are the option values
    const isYes = selectedValue === 'yes';
    if (!onChange) return;

    if (returnType === 'boolean') {
      onChange(isYes);
      return;
    }

    if (stringFormat === 'S/N') {
      onChange(isYes ? 'S' : 'N');
    } else {
      onChange(isYes ? 'yes' : 'no');
    }
  };

  return (
    <Select
      id={id}
      value={displayValue}
      placeholder={placeholder || t('messages.select')}
      onChange={handleChange}
      disabled={disabled}
      allowClear={allowClear}
      size={size}
      style={style}
      className={className}
    >
      <Select.Option value="yes" title={t('messages.yes')}>
        {t('messages.yes')}
      </Select.Option>
      <Select.Option value="no" title={t('messages.no')}>
        {t('messages.no')}
      </Select.Option>
    </Select>
  );
};

export default SelectYesNo;
